import gql from "graphql-tag";
import { useCallback, useContext } from "react";
import { ApolloError, FetchResult } from "@apollo/client";
import { useMutation } from "@apollo/react-hooks";
import { cartFragment } from "src/hooks/useCart";
import { CartUpdateAction, Mutation } from "src/types/ctgraphql.d";
import { useCreateCart } from "./useCreateCart";
import { AppCtx } from "src/contexts/app.context";
import { CartInfo } from "src/types/cartInfo";
import { logger } from "src/utils/helper/logger";

export interface UpdateCartData {
  updateMyCart: Mutation["updateMyCart"];
}

export interface UpdateCartProps {
  refetchQueries?: string[];
  onCompleted?: (updateCartData: UpdateCartData) => void;
  onError?: (updateCartError: ApolloError) => void;
}

interface UpdateCartVars {
  id: string;
  version: number;
  locale: string;
  actions: CartUpdateAction[];
}

interface UpdateCartHook {
  data?: UpdateCartData;
  loading: boolean;
  error?: ApolloError;
  updateCart: (props: {
    actions: CartUpdateAction[];
    cartInfo: CartInfo;
  }) => Promise<FetchResult<UpdateCartData> | undefined>;
}

const UPDATE_CART = gql`
  mutation updateMyCart(
    $id: String!
    $version: Long!
    $locale: Locale!
    $actions: [MyCartUpdateAction!]!
  ) {
    updateMyCart(id: $id, version: $version, actions: $actions) {
      ...cart
    }
  }

  ${cartFragment}
`;

function useUpdateCart({
  refetchQueries = [],
  onCompleted,
  onError,
}: UpdateCartProps): UpdateCartHook {
  const { locale, storeConfig, setCartMinLineItemsTotalPriceDiff } =
    useContext(AppCtx);

  const [updateCartMutation, { data, loading, error }] = useMutation<
    UpdateCartData,
    UpdateCartVars
  >(UPDATE_CART, {
    refetchQueries,
    onCompleted: (updateCartData) => {
      setCartMinLineItemsTotalPriceDiff(
        updateCartData?.updateMyCart?.lineItems
      );

      if (onCompleted) {
        onCompleted(updateCartData);
      }
    },
    onError: (apolloError) => {
      const versionMisMatchError = apolloError.graphQLErrors.find(
        (gqlErr) => gqlErr?.extensions?.code === "ConcurrentModification"
      );

      if (onError && !versionMisMatchError) {
        onError(apolloError);
      }
    },
  });

  const { createCart } = useCreateCart();

  const updateCart: UpdateCartHook["updateCart"] = useCallback(
    async ({ cartInfo, actions }) => {
      if (!actions.length) {
        return;
      }

      try {
        let createdCartData;
        if (
          !cartInfo ||
          !cartInfo.id ||
          (cartInfo.version === null && cartInfo.version === undefined)
        ) {
          createdCartData = await createCart({
            currency: storeConfig.currencyCode,
          });
        }

        const updatedCart = await updateCartMutation({
          variables: {
            id: createdCartData?.data?.createMyCart?.id || cartInfo?.id || "",
            version:
              (createdCartData?.data?.createMyCart?.version as number) ||
              cartInfo?.version ||
              0,
            locale,
            actions,
          },
          context: { useCT: true },
        });

        return updatedCart;
      } catch (error) {
        logger({ message: "Error updating cart:", error: error as Error });
        return;
      }
    },
    [updateCartMutation, createCart, locale, storeConfig.currencyCode]
  );

  return { updateCart, data, loading, error } as UpdateCartHook;
}

export { useUpdateCart };
